/**
 * Scan-path panel: up to three fair/poor interpretation constraints.
 */
import { Box, Chip, Stack, Typography } from "@mui/material";
import type { SoilInterpretationDTO } from "@flaha/shared-types";

import { GlassSurface } from "../../../layouts/components/GlassSurface";
import { selectTopConstraints } from "../utils/topConstraints";
import { FieldHelp } from "./FieldHelp";
import { SpecialNote } from "./SpecialNote";

interface TopConstraintsPanelProps {
	interpretation: SoilInterpretationDTO | null | undefined;
}

export function TopConstraintsPanel({ interpretation }: TopConstraintsPanelProps) {
	const constraints = selectTopConstraints(interpretation, 3);

	if (!interpretation) {
		return (
			<SpecialNote kind="limitation" title="Interpretation not available yet">
				Constraints will appear here once agronomic interpretation is ready
				for this test.
			</SpecialNote>
		);
	}

	if (constraints.length === 0) {
		return (
			<SpecialNote kind="guidance" title="No major constraints highlighted">
				Interpretation did not flag fair or poor risk categories for the
				scanned fields. Review the full interpretation panel for the complete
				picture.
			</SpecialNote>
		);
	}

	return (
		<GlassSurface
			component="section"
			aria-labelledby="top-constraints-title"
			intensity="standard"
			depth="raised"
			sx={{ p: { xs: 2, md: 2.5 }, height: "100%" }}
		>
			<Stack spacing={1.75}>
				<Box>
					<Typography
						id="top-constraints-title"
						component="h2"
						variant="overline"
						sx={{ color: "workspace.bronze", letterSpacing: "0.1em", fontWeight: 700 }}
					>
						Start here
					</Typography>
					<Typography component="p" variant="h6" sx={{ mt: 0.25 }}>
						Top constraints to manage
					</Typography>
					<Typography variant="body2" sx={{ color: "workspace.textMuted", mt: 0.5 }}>
						Highest-priority fair and poor signals from the interpretation
						(max three).
					</Typography>
				</Box>
				<Stack spacing={1.25}>
					{constraints.map((item) => (
						<Box
							key={item.field}
							sx={{
								p: 1.5,
								borderRadius: 1.25,
								border: "1px solid",
								borderColor:
									item.status.tone === "poor"
										? "rgba(180, 35, 24, 0.35)"
										: "rgba(216, 132, 0, 0.3)",
								backgroundColor:
									item.status.tone === "poor"
										? "rgba(180, 35, 24, 0.08)"
										: "rgba(216, 132, 0, 0.08)",
							}}
						>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								spacing={1}
							>
								<Typography variant="subtitle2" fontWeight={700}>
									{item.label}
								</Typography>
								<Chip
									label={item.value}
									color={item.status.color}
									size="small"
								/>
							</Stack>
							{item.help ? (
								<FieldHelp summary={item.help} dense />
							) : null}
						</Box>
					))}
				</Stack>
			</Stack>
		</GlassSurface>
	);
}
